import { defineMessages } from './define'

/* The front desk: patient search and registration, the patient record and
 * its correction, starting a visit, the records list, and the messages of
 * the live form checks (validate.ts) that every mobile form shares. */

export default defineMessages({
  en: {
    'desk.doneChip': 'Done',
    'desk.allergyChip': 'Allergy',
    'desk.collectPayment': 'Collect payment',
    // form labels
    'desk.fullName': 'Full name',
    'desk.mobileNumber': 'Mobile number',
    'desk.altMobile': 'Alternate mobile',
    'desk.months': 'Months',
    'desk.ageYears': 'Age (years)',
    'desk.monthsInfants': 'Months (infants)',
    'desk.city': 'City',
    'desk.pin': 'PIN',
    'desk.guardian': 'Guardian',
    'desk.referredBy': 'Referred by',
    'desk.knownAllergies': 'Known allergies',
    'desk.allergiesPh': 'Penicillin, sulpha…',
    'desk.allergyHint': 'Shown in red on every screen and on the prescription.',
    'desk.chronicConditions': 'Chronic conditions',
    'desk.chronicPh': 'Diabetes, hypertension…',
    'desk.category': 'Patient category',
    'desk.categoryDiabetes': 'Diabetic',
    'desk.categoryNonDiabetic': 'Non-Diabetic',
    'desk.categoryNotSet': 'Not set',
    'desk.vCategory': 'Choose Diabetic or Non-Diabetic.',
    'desk.allCategories': 'All patients',
    'desk.categoryHint': 'Pick one. Diabetic and Non-Diabetic are kept separate.',
    'desk.currentMedication': 'Current medication',
    'desk.notKnown': 'Not known',
    'desk.fixRed': 'Fix the fields marked in red first.',
    // registration
    'desk.newPatient': 'New patient',
    'desk.regNote': 'Only name, category and gender are required; the age and mobile can stay empty. Mistakes are flagged as you type.',
    'desk.registering': 'Registering…',
    'desk.registerPatient': 'Register patient',
    'desk.registerNew': 'Register a new patient',
    'desk.registered': 'Registered {name} — {code}',
    'desk.registeredDupOne': 'Registered {code}. Looks like an existing patient.',
    'desk.registeredDupOther': 'Registered {code}. Looks like {n} existing patients.',
    'desk.registeredSimilar_one': 'Registered {code}. {n} similar record already on file.',
    'desk.registeredSimilar_other': 'Registered {code}. {n} similar records already on file.',
    'desk.couldNotRegister': 'Could not register.',
    'desk.quickNote': 'Just enough to book. The rest can be added from their record later.',
    'desk.registerPick': 'Register & pick',
    // starting a visit
    'desk.startVisit': 'Start a visit',
    'desk.opening': 'Opening…',
    'desk.openVisitPrint': 'Open visit & print token',
    'desk.change': 'Change',
    'desk.viewFullRecord': 'View full record',
    'desk.samePerson': 'Is this the same person?',
    'desk.dupNote': 'These records already share this mobile number or name.',
    'desk.lookItUp': 'Look it up',
    'desk.dupAdvice': 'If one of them is the patient in front of you, use that record and ask an administrator to remove the one just created — the history hangs off the record, not the name.',
    'desk.differentPerson': 'Different person, carry on',
    'desk.allergyValue': 'Allergy: {text}',
    'desk.chronicValue': 'Chronic: {text}',
    'desk.followUpRate': 'follow-up rate, within {n} days',
    'desk.emergencyOverrides': 'Emergency overrides this — the visit bills at the consultation rate.',
    'desk.chiefComplaint': 'Chief complaint',
    'desk.complaintPh': 'Headache, 3 days',
    'desk.alreadyInQueue': '{name} is already in the queue — token #{token}',
    'desk.tokenFor': 'Token #{token} — {name}',
    'desk.couldNotOpenVisit': 'Could not open the visit.',
    // search and queue
    'desk.searchPatients': 'Search patients',
    'desk.searchPh': 'Mobile number, name or patient code',
    'desk.clear': 'Clear',
    'desk.matches': 'Matches',
    'desk.checkAgeNote': 'Check the age and last visit before choosing. Families often share one mobile number.',
    'desk.visitCount_one': '{n} visit',
    'desk.visitCount_other': '{n} visits',
    'desk.lastOn': 'last {date}',
    'desk.noMatch': 'No patient matches “{q}”',
    'desk.todaysQueue': 'Today’s queue',
    'desk.queueOn': 'Queue · {date}',
    'desk.countOf': '{n} of {total}',
    'desk.waitingCount': '{n} waiting',
    'desk.nowAndWaiting': 'Now {token} · {n} waiting',
    'desk.queueDate': 'Queue date',
    'desk.couldNotLoadDay': 'Could not load that day.',
    'desk.loadingDay': 'Loading {date}…',
    'desk.noVisitsToday': 'No visits yet today',
    'desk.noVisitsOn': 'No visits on {date}',
    'desk.searchAboveHint': 'Search above to find any patient on record.',
    'desk.noFilterMatch': 'Nobody matches these filters',
    'desk.doctorQueue': '{name} queue',
    'desk.vitals': 'Vitals',
    'desk.record': 'Record',
    'desk.unpaidPill': 'unpaid',
    // filters
    'desk.filterStatus': 'Filter by status',
    'desk.filterDoctor': 'Filter by doctor',
    'desk.filterPayment': 'Filter by payment',
    'desk.filterVisitType': 'Filter by visit type',
    'desk.filterRecords': 'Filter records',
    'desk.allDoctors': 'All doctors',
    'desk.allDoctorsCount': 'All doctors · {n}',
    'desk.anyPayment': 'Any payment',
    'desk.anyVisitType': 'Any visit type',
    'desk.anyStatus': 'Any status',
    'desk.refunded': 'Refunded',
    'desk.moreFilters': 'More filters',
    'desk.clearFilters': 'Clear filters',
    'desk.fromDate': 'From date',
    'desk.toDate': 'To date',
    'desk.diagnosis': 'Diagnosis',
    'desk.diagnosisContains': 'Diagnosis contains',
    // patient record
    'desk.patientNotFound': 'Patient not found.',
    'desk.bookAppointment': 'Book appointment',
    'desk.editPatient': 'Edit patient',
    'desk.fullRecord': 'Full record',
    'desk.noVisitsYet': 'No visits on record yet.',
    'desk.visitNo': 'Visit {n}',
    'desk.recordUpdated': 'Patient record updated.',
    'desk.couldNotSave': 'Could not save.',
    'desk.saveChanges': 'Save changes',
    'desk.auditNote': 'Every change is written to the audit log with what it was before, so a correction is never a quiet edit.',
    'desk.allergyOnFile': 'Allergy on file: {text}',
    // records
    'desk.records': 'Records',
    'desk.timeline': 'Timeline — {name}',
    'desk.timelineSub': 'Every visit on this patient’s file.',
    'desk.timelineTitle': 'Timeline',
    'desk.timelineFor': 'Timeline for {name}',
    'desk.openRecordOf': 'Open {name}’s record',
    'desk.thisPatient': 'this patient',
    'desk.allVisits': 'All visits',
    'desk.searchRecords': 'Search records',
    'desk.recordsPh': 'Name, mobile or code',
    'desk.exportCsv': 'Export CSV',
    'desk.exportFailed': 'Export failed.',
    'desk.couldNotLoadRecords': 'Could not load records.',
    'desk.noVisitsMatch': 'No visits match.',
    'desk.serialAria': 'Serial {token}, {name}, {status}',
    'desk.nthVisit': '{ord} visit',
    'desk.notBilled': 'Not billed',
    'desk.moneyRefunded': '{fee} · refunded',
    'desk.moneyPaid': '{fee} · paid',
    'desk.moneyPart': '{paid} of {fee} · {due} due',
    'desk.moneyUnpaid': '{fee} · unpaid',
    'desk.prev': 'Prev',
    'desk.pageOf': 'Page {page} of {pages}',
    // validation (validate.ts)
    'desk.vRequired': '{label} is required.',
    'desk.vTooShort': '{label} is too short.',
    'desk.vTooLong': '{label} is too long.',
    'desk.vTooLongCount': '{label} is too long ({n}/{max}).',
    'desk.vNameChars': '{label} can only hold letters, spaces, dots and hyphens.',
    'desk.vMobileRequired': 'Mobile number is required.',
    'desk.vDigitsOnly': 'Use digits only.',
    'desk.vMobileDigits': 'Enter 10 digits ({n} so far).',
    'desk.vMobileStart': 'An Indian mobile starts with 6, 7, 8 or 9.',
    'desk.vWholeNumber': '{label} must be a whole number.',
    'desk.vBetween': '{label} must be between {min} and {max}.',
    'desk.vAmount': '{label} must be an amount like 350 or 350.50.',
    'desk.vPincode': 'A PIN code is six digits.',
    'desk.vEmail': 'That does not look like an email address.',
    'desk.vAfterStart': '{label} must be after the start.',
    'desk.vNotPast': '{label} cannot be in the past.',
    'desk.vEnd': 'End',
    'desk.vAgeOrMonths': 'Give an age in years, or months for an infant.',
  },
  hi: {
    'desk.doneChip': 'पूरा',
    'desk.allergyChip': 'एलर्जी',
    'desk.collectPayment': 'भुगतान लें',
    'desk.fullName': 'पूरा नाम',
    'desk.mobileNumber': 'मोबाइल नंबर',
    'desk.altMobile': 'दूसरा मोबाइल',
    'desk.months': 'महीने',
    'desk.ageYears': 'उम्र (साल)',
    'desk.monthsInfants': 'महीने (शिशु)',
    'desk.city': 'शहर',
    'desk.pin': 'PIN कोड',
    'desk.guardian': 'अभिभावक',
    'desk.referredBy': 'रेफ़र करने वाले',
    'desk.knownAllergies': 'ज्ञात एलर्जी',
    'desk.allergiesPh': 'पेनिसिलिन, सल्फ़ा…',
    'desk.allergyHint': 'हर स्क्रीन पर और पर्चे पर लाल रंग में दिखती है।',
    'desk.chronicConditions': 'पुरानी बीमारियाँ',
    'desk.chronicPh': 'डायबिटीज़, हाई ब्लड प्रेशर…',
    'desk.category': 'मरीज़ की श्रेणी',
    'desk.categoryDiabetes': 'मधुमेह',
    'desk.categoryNonDiabetic': 'गैर-मधुमेह',
    'desk.categoryNotSet': 'तय नहीं',
    'desk.vCategory': 'मधुमेह या गैर-मधुमेह चुनें।',
    'desk.allCategories': 'सभी मरीज़',
    'desk.categoryHint': 'एक चुनें। मधुमेह और गैर-मधुमेह अलग-अलग रखे जाते हैं।',
    'desk.currentMedication': 'अभी चल रही दवाइयाँ',
    'desk.notKnown': 'पता नहीं',
    'desk.fixRed': 'पहले लाल निशान वाली जगहें ठीक करें।',
    'desk.newPatient': 'नया मरीज़',
    'desk.regNote': 'केवल नाम, श्रेणी और लिंग ज़रूरी हैं; उम्र और मोबाइल खाली छोड़े जा सकते हैं। गलती लिखते समय ही दिखा दी जाती है।',
    'desk.registering': 'रजिस्टर हो रहा है…',
    'desk.registerPatient': 'मरीज़ रजिस्टर करें',
    'desk.registerNew': 'नया मरीज़ रजिस्टर करें',
    'desk.registered': '{name} रजिस्टर हो गए — {code}',
    'desk.registeredDupOne': '{code} रजिस्टर हो गया। यह किसी पुराने मरीज़ जैसा लगता है।',
    'desk.registeredDupOther': '{code} रजिस्टर हो गया। यह {n} पुराने मरीज़ों जैसा लगता है।',
    'desk.registeredSimilar_one': '{code} रजिस्टर हो गया। {n} मिलता-जुलता रिकॉर्ड पहले से मौजूद है।',
    'desk.registeredSimilar_other': '{code} रजिस्टर हो गया। {n} मिलते-जुलते रिकॉर्ड पहले से मौजूद हैं।',
    'desk.couldNotRegister': 'रजिस्टर नहीं हो सका।',
    'desk.quickNote': 'बुकिंग के लिए इतना काफ़ी है। बाकी जानकारी बाद में उनके रिकॉर्ड में जोड़ी जा सकती है।',
    'desk.registerPick': 'रजिस्टर करें और चुनें',
    'desk.startVisit': 'विज़िट शुरू करें',
    'desk.opening': 'खुल रहा है…',
    'desk.openVisitPrint': 'विज़िट खोलें और टोकन प्रिंट करें',
    'desk.change': 'बदलें',
    'desk.viewFullRecord': 'पूरा रिकॉर्ड देखें',
    'desk.samePerson': 'क्या यह वही व्यक्ति है?',
    'desk.dupNote': 'इन रिकॉर्ड में पहले से यही मोबाइल नंबर या नाम है।',
    'desk.lookItUp': 'खोलकर देखें',
    'desk.dupAdvice': 'अगर इनमें से कोई आपके सामने खड़ा मरीज़ है, तो वही रिकॉर्ड इस्तेमाल करें और अभी बनाया गया रिकॉर्ड हटाने के लिए एडमिनिस्ट्रेटर से कहें — इतिहास रिकॉर्ड से जुड़ा होता है, नाम से नहीं।',
    'desk.differentPerson': 'अलग व्यक्ति है, आगे बढ़ें',
    'desk.allergyValue': 'एलर्जी: {text}',
    'desk.chronicValue': 'पुरानी बीमारी: {text}',
    'desk.followUpRate': 'फ़ॉलो-अप फ़ीस, {n} दिन के अंदर',
    'desk.emergencyOverrides': 'इमरजेंसी में यह लागू नहीं होगा — विज़िट की फ़ीस कंसल्टेशन रेट पर लगेगी।',
    'desk.chiefComplaint': 'मुख्य तकलीफ़',
    'desk.complaintPh': 'सिरदर्द, 3 दिन से',
    'desk.alreadyInQueue': '{name} पहले से कतार में हैं — टोकन #{token}',
    'desk.tokenFor': 'टोकन #{token} — {name}',
    'desk.couldNotOpenVisit': 'विज़िट नहीं खुल सकी।',
    'desk.searchPatients': 'मरीज़ खोजें',
    'desk.searchPh': 'मोबाइल नंबर, नाम या मरीज़ कोड',
    'desk.clear': 'साफ़ करें',
    'desk.matches': 'मिले हुए मरीज़',
    'desk.checkAgeNote': 'चुनने से पहले उम्र और पिछली विज़िट देख लें। अक्सर पूरे परिवार का एक ही मोबाइल नंबर होता है।',
    'desk.visitCount_one': '{n} विज़िट',
    'desk.visitCount_other': '{n} विज़िट',
    'desk.lastOn': 'पिछली {date}',
    'desk.noMatch': '“{q}” से कोई मरीज़ नहीं मिला',
    'desk.todaysQueue': 'आज की कतार',
    'desk.queueOn': 'कतार · {date}',
    'desk.countOf': '{total} में से {n}',
    'desk.waitingCount': '{n} इंतज़ार में',
    'desk.nowAndWaiting': 'अभी {token} · {n} इंतज़ार में',
    'desk.queueDate': 'कतार की तारीख',
    'desk.couldNotLoadDay': 'उस दिन की सूची लोड नहीं हो सकी।',
    'desk.loadingDay': '{date} लोड हो रहा है…',
    'desk.noVisitsToday': 'आज अभी तक कोई विज़िट नहीं',
    'desk.noVisitsOn': '{date} को कोई विज़िट नहीं',
    'desk.searchAboveHint': 'रिकॉर्ड में किसी भी मरीज़ को ढूँढने के लिए ऊपर खोजें।',
    'desk.noFilterMatch': 'इन फ़िल्टर से कोई नहीं मिला',
    'desk.doctorQueue': '{name} की कतार',
    'desk.vitals': 'वाइटल्स',
    'desk.record': 'रिकॉर्ड',
    'desk.unpaidPill': 'बाकी',
    'desk.filterStatus': 'स्थिति से छाँटें',
    'desk.filterDoctor': 'डॉक्टर से छाँटें',
    'desk.filterPayment': 'भुगतान से छाँटें',
    'desk.filterVisitType': 'विज़िट के प्रकार से छाँटें',
    'desk.filterRecords': 'रिकॉर्ड छाँटें',
    'desk.allDoctors': 'सभी डॉक्टर',
    'desk.allDoctorsCount': 'सभी डॉक्टर · {n}',
    'desk.anyPayment': 'कोई भी भुगतान',
    'desk.anyVisitType': 'किसी भी प्रकार की विज़िट',
    'desk.anyStatus': 'कोई भी स्थिति',
    'desk.refunded': 'वापस किया गया',
    'desk.moreFilters': 'और फ़िल्टर',
    'desk.clearFilters': 'फ़िल्टर हटाएँ',
    'desk.fromDate': 'शुरू की तारीख',
    'desk.toDate': 'आखिरी तारीख',
    'desk.diagnosis': 'निदान',
    'desk.diagnosisContains': 'निदान में शामिल',
    'desk.patientNotFound': 'मरीज़ नहीं मिला।',
    'desk.bookAppointment': 'अपॉइंटमेंट बुक करें',
    'desk.editPatient': 'मरीज़ की जानकारी बदलें',
    'desk.fullRecord': 'पूरा रिकॉर्ड',
    'desk.noVisitsYet': 'अभी तक कोई विज़िट दर्ज नहीं।',
    'desk.visitNo': 'विज़िट {n}',
    'desk.recordUpdated': 'मरीज़ का रिकॉर्ड अपडेट हो गया।',
    'desk.couldNotSave': 'सेव नहीं हो सका।',
    'desk.saveChanges': 'बदलाव सेव करें',
    'desk.auditNote': 'हर बदलाव पुरानी जानकारी के साथ ऑडिट लॉग में लिखा जाता है, इसलिए कोई भी सुधार चुपचाप नहीं होता।',
    'desk.allergyOnFile': 'रिकॉर्ड में एलर्जी: {text}',
    'desk.records': 'रिकॉर्ड',
    'desk.timeline': 'टाइमलाइन — {name}',
    'desk.timelineSub': 'इस मरीज़ की फ़ाइल की हर विज़िट।',
    'desk.timelineTitle': 'टाइमलाइन',
    'desk.timelineFor': '{name} की टाइमलाइन',
    'desk.openRecordOf': '{name} का रिकॉर्ड खोलें',
    'desk.thisPatient': 'यह मरीज़',
    'desk.allVisits': 'सभी विज़िट',
    'desk.searchRecords': 'रिकॉर्ड खोजें',
    'desk.recordsPh': 'नाम, मोबाइल या कोड',
    'desk.exportCsv': 'CSV एक्सपोर्ट करें',
    'desk.exportFailed': 'एक्सपोर्ट नहीं हो सका।',
    'desk.couldNotLoadRecords': 'रिकॉर्ड लोड नहीं हो सके।',
    'desk.noVisitsMatch': 'कोई विज़िट नहीं मिली।',
    'desk.serialAria': 'क्रम {token}, {name}, {status}',
    'desk.nthVisit': 'विज़िट नं. {n}',
    'desk.notBilled': 'बिल नहीं बना',
    'desk.moneyRefunded': '{fee} · वापस किया गया',
    'desk.moneyPaid': '{fee} · भुगतान हो गया',
    'desk.moneyPart': '{fee} में से {paid} · {due} बाकी',
    'desk.moneyUnpaid': '{fee} · बाकी',
    'desk.prev': 'पिछला',
    'desk.pageOf': 'पेज {page} / {pages}',
    'desk.vRequired': '{label} ज़रूरी है।',
    'desk.vTooShort': '{label} बहुत छोटा है।',
    'desk.vTooLong': '{label} बहुत लंबा है।',
    'desk.vTooLongCount': '{label} बहुत लंबा है ({n}/{max})।',
    'desk.vNameChars': '{label} में केवल अक्षर, स्पेस, बिंदु और हाइफ़न हो सकते हैं।',
    'desk.vMobileRequired': 'मोबाइल नंबर ज़रूरी है।',
    'desk.vDigitsOnly': 'केवल अंक लिखें।',
    'desk.vMobileDigits': '10 अंक लिखें (अभी {n} हैं)।',
    'desk.vMobileStart': 'भारतीय मोबाइल नंबर 6, 7, 8 या 9 से शुरू होता है।',
    'desk.vWholeNumber': '{label} पूरी संख्या होनी चाहिए।',
    'desk.vBetween': '{label} {min} से {max} के बीच होना चाहिए।',
    'desk.vAmount': '{label} 350 या 350.50 जैसी रकम होनी चाहिए।',
    'desk.vPincode': 'PIN कोड छह अंकों का होता है।',
    'desk.vEmail': 'यह सही ईमेल पता नहीं लगता।',
    'desk.vAfterStart': '{label} शुरुआत के बाद होना चाहिए।',
    'desk.vNotPast': '{label} बीते समय में नहीं हो सकता।',
    'desk.vEnd': 'समाप्ति',
    'desk.vAgeOrMonths': 'उम्र सालों में लिखें, या शिशु के लिए महीनों में।',
  },
  mr: {
    'desk.doneChip': 'पूर्ण',
    'desk.allergyChip': 'ॲलर्जी',
    'desk.collectPayment': 'पेमेंट घ्या',
    'desk.fullName': 'पूर्ण नाव',
    'desk.mobileNumber': 'मोबाइल नंबर',
    'desk.altMobile': 'दुसरा मोबाइल',
    'desk.months': 'महिने',
    'desk.ageYears': 'वय (वर्षे)',
    'desk.monthsInfants': 'महिने (बाळ)',
    'desk.city': 'शहर',
    'desk.pin': 'PIN कोड',
    'desk.guardian': 'पालक',
    'desk.referredBy': 'रेफर करणारे',
    'desk.knownAllergies': 'माहीत असलेली ॲलर्जी',
    'desk.allergiesPh': 'पेनिसिलिन, सल्फा…',
    'desk.allergyHint': 'प्रत्येक स्क्रीनवर आणि प्रिस्क्रिप्शनवर लाल रंगात दिसते.',
    'desk.chronicConditions': 'दीर्घ आजार',
    'desk.chronicPh': 'मधुमेह, उच्च रक्तदाब…',
    'desk.category': 'रुग्णाचा गट',
    'desk.categoryDiabetes': 'मधुमेह',
    'desk.categoryNonDiabetic': 'मधुमेह नाही',
    'desk.categoryNotSet': 'निश्चित नाही',
    'desk.vCategory': 'मधुमेह किंवा मधुमेह नाही निवडा.',
    'desk.allCategories': 'सर्व रुग्ण',
    'desk.categoryHint': 'एक निवडा. मधुमेह आणि मधुमेह नाही वेगळे ठेवले जातात.',
    'desk.currentMedication': 'सध्या सुरू असलेली औषधे',
    'desk.notKnown': 'माहीत नाही',
    'desk.fixRed': 'आधी लाल खूण असलेली माहिती दुरुस्त करा.',
    'desk.newPatient': 'नवीन रुग्ण',
    'desk.regNote': 'फक्त नाव, गट आणि लिंग आवश्यक आहे; वय आणि मोबाइल रिकामे ठेवता येतात. चूक लिहितानाच दाखवली जाते.',
    'desk.registering': 'रजिस्टर होत आहे…',
    'desk.registerPatient': 'रुग्ण रजिस्टर करा',
    'desk.registerNew': 'नवीन रुग्ण रजिस्टर करा',
    'desk.registered': '{name} यांचे रजिस्ट्रेशन झाले — {code}',
    'desk.registeredDupOne': '{code} रजिस्टर झाले. हे आधीच्या एका रुग्णासारखे दिसते.',
    'desk.registeredDupOther': '{code} रजिस्टर झाले. हे आधीच्या {n} रुग्णांसारखे दिसते.',
    'desk.registeredSimilar_one': '{code} रजिस्टर झाले. {n} मिळताजुळता रेकॉर्ड आधीच आहे.',
    'desk.registeredSimilar_other': '{code} रजिस्टर झाले. {n} मिळतेजुळते रेकॉर्ड आधीच आहेत.',
    'desk.couldNotRegister': 'रजिस्टर करता आले नाही.',
    'desk.quickNote': 'बुकिंगसाठी इतके पुरेसे आहे. बाकी माहिती नंतर त्यांच्या रेकॉर्डमध्ये भरता येईल.',
    'desk.registerPick': 'रजिस्टर करा आणि निवडा',
    'desk.startVisit': 'भेट सुरू करा',
    'desk.opening': 'उघडत आहे…',
    'desk.openVisitPrint': 'भेट उघडा आणि टोकन प्रिंट करा',
    'desk.change': 'बदला',
    'desk.viewFullRecord': 'पूर्ण रेकॉर्ड पाहा',
    'desk.samePerson': 'ही तीच व्यक्ती आहे का?',
    'desk.dupNote': 'या रेकॉर्डमध्ये आधीच हाच मोबाइल नंबर किंवा नाव आहे.',
    'desk.lookItUp': 'उघडून पाहा',
    'desk.dupAdvice': 'यापैकी एखादा तुमच्यासमोरचा रुग्ण असेल, तर तोच रेकॉर्ड वापरा आणि आत्ता बनवलेला रेकॉर्ड काढण्यासाठी ॲडमिनिस्ट्रेटरला सांगा — इतिहास रेकॉर्डशी जोडलेला असतो, नावाशी नाही.',
    'desk.differentPerson': 'वेगळी व्यक्ती आहे, पुढे चला',
    'desk.allergyValue': 'ॲलर्जी: {text}',
    'desk.chronicValue': 'दीर्घ आजार: {text}',
    'desk.followUpRate': 'फॉलो-अप फी, {n} दिवसांच्या आत',
    'desk.emergencyOverrides': 'इमर्जन्सीमध्ये हे लागू होणार नाही — भेटीचे शुल्क कन्सल्टेशन दराने लागेल.',
    'desk.chiefComplaint': 'मुख्य तक्रार',
    'desk.complaintPh': 'डोकेदुखी, 3 दिवसांपासून',
    'desk.alreadyInQueue': '{name} आधीच रांगेत आहेत — टोकन #{token}',
    'desk.tokenFor': 'टोकन #{token} — {name}',
    'desk.couldNotOpenVisit': 'भेट उघडता आली नाही.',
    'desk.searchPatients': 'रुग्ण शोधा',
    'desk.searchPh': 'मोबाइल नंबर, नाव किंवा रुग्ण कोड',
    'desk.clear': 'साफ करा',
    'desk.matches': 'सापडलेले रुग्ण',
    'desk.checkAgeNote': 'निवडण्याआधी वय आणि मागची भेट तपासा. अनेकदा पूर्ण कुटुंबाचा एकच मोबाइल नंबर असतो.',
    'desk.visitCount_one': '{n} भेट',
    'desk.visitCount_other': '{n} भेटी',
    'desk.lastOn': 'मागील {date}',
    'desk.noMatch': '“{q}” शी जुळणारा रुग्ण नाही',
    'desk.todaysQueue': 'आजची रांग',
    'desk.queueOn': 'रांग · {date}',
    'desk.countOf': '{total} पैकी {n}',
    'desk.waitingCount': '{n} प्रतीक्षेत',
    'desk.nowAndWaiting': 'आता {token} · {n} प्रतीक्षेत',
    'desk.queueDate': 'रांगेची तारीख',
    'desk.couldNotLoadDay': 'त्या दिवसाची यादी लोड झाली नाही.',
    'desk.loadingDay': '{date} लोड होत आहे…',
    'desk.noVisitsToday': 'आज अजून एकही भेट नाही',
    'desk.noVisitsOn': '{date} रोजी एकही भेट नाही',
    'desk.searchAboveHint': 'रेकॉर्डमधील कोणताही रुग्ण शोधण्यासाठी वर शोधा.',
    'desk.noFilterMatch': 'या फिल्टरनुसार कोणीही नाही',
    'desk.doctorQueue': '{name} यांची रांग',
    'desk.vitals': 'व्हायटल्स',
    'desk.record': 'रेकॉर्ड',
    'desk.unpaidPill': 'बाकी',
    'desk.filterStatus': 'स्थितीनुसार निवडा',
    'desk.filterDoctor': 'डॉक्टरनुसार निवडा',
    'desk.filterPayment': 'पेमेंटनुसार निवडा',
    'desk.filterVisitType': 'भेटीच्या प्रकारानुसार निवडा',
    'desk.filterRecords': 'रेकॉर्ड निवडा',
    'desk.allDoctors': 'सर्व डॉक्टर',
    'desk.allDoctorsCount': 'सर्व डॉक्टर · {n}',
    'desk.anyPayment': 'कोणतेही पेमेंट',
    'desk.anyVisitType': 'कोणत्याही प्रकारची भेट',
    'desk.anyStatus': 'कोणतीही स्थिती',
    'desk.refunded': 'परत केले',
    'desk.moreFilters': 'आणखी फिल्टर',
    'desk.clearFilters': 'फिल्टर काढा',
    'desk.fromDate': 'सुरुवातीची तारीख',
    'desk.toDate': 'शेवटची तारीख',
    'desk.diagnosis': 'निदान',
    'desk.diagnosisContains': 'निदानात असलेले',
    'desk.patientNotFound': 'रुग्ण सापडला नाही.',
    'desk.bookAppointment': 'अपॉइंटमेंट बुक करा',
    'desk.editPatient': 'रुग्णाची माहिती बदला',
    'desk.fullRecord': 'पूर्ण रेकॉर्ड',
    'desk.noVisitsYet': 'अजून एकही भेट नोंदलेली नाही.',
    'desk.visitNo': 'भेट {n}',
    'desk.recordUpdated': 'रुग्णाचा रेकॉर्ड अपडेट झाला.',
    'desk.couldNotSave': 'सेव्ह झाले नाही.',
    'desk.saveChanges': 'बदल सेव्ह करा',
    'desk.auditNote': 'प्रत्येक बदल आधीच्या माहितीसह ऑडिट लॉगमध्ये नोंदवला जातो, त्यामुळे कोणतीही दुरुस्ती गुपचूप होत नाही.',
    'desk.allergyOnFile': 'रेकॉर्डमधील ॲलर्जी: {text}',
    'desk.records': 'रेकॉर्ड',
    'desk.timeline': 'टाइमलाइन — {name}',
    'desk.timelineSub': 'या रुग्णाच्या फाइलमधील प्रत्येक भेट.',
    'desk.timelineTitle': 'टाइमलाइन',
    'desk.timelineFor': '{name} यांची टाइमलाइन',
    'desk.openRecordOf': '{name} यांचा रेकॉर्ड उघडा',
    'desk.thisPatient': 'हा रुग्ण',
    'desk.allVisits': 'सर्व भेटी',
    'desk.searchRecords': 'रेकॉर्ड शोधा',
    'desk.recordsPh': 'नाव, मोबाइल किंवा कोड',
    'desk.exportCsv': 'CSV एक्सपोर्ट करा',
    'desk.exportFailed': 'एक्सपोर्ट झाले नाही.',
    'desk.couldNotLoadRecords': 'रेकॉर्ड लोड झाले नाहीत.',
    'desk.noVisitsMatch': 'एकही भेट जुळली नाही.',
    'desk.serialAria': 'अनुक्रमांक {token}, {name}, {status}',
    'desk.nthVisit': 'भेट क्र. {n}',
    'desk.notBilled': 'बिल केलेले नाही',
    'desk.moneyRefunded': '{fee} · परत केले',
    'desk.moneyPaid': '{fee} · पैसे भरले',
    'desk.moneyPart': '{fee} पैकी {paid} · {due} बाकी',
    'desk.moneyUnpaid': '{fee} · बाकी',
    'desk.prev': 'मागे',
    'desk.pageOf': 'पान {page} / {pages}',
    'desk.vRequired': '{label} आवश्यक आहे.',
    'desk.vTooShort': '{label} खूप लहान आहे.',
    'desk.vTooLong': '{label} खूप मोठे आहे.',
    'desk.vTooLongCount': '{label} खूप मोठे आहे ({n}/{max}).',
    'desk.vNameChars': '{label} मध्ये फक्त अक्षरे, स्पेस, टिंब आणि हायफन चालतात.',
    'desk.vMobileRequired': 'मोबाइल नंबर आवश्यक आहे.',
    'desk.vDigitsOnly': 'फक्त अंक लिहा.',
    'desk.vMobileDigits': '10 अंक लिहा (आत्ता {n} आहेत).',
    'desk.vMobileStart': 'भारतीय मोबाइल नंबर 6, 7, 8 किंवा 9 ने सुरू होतो.',
    'desk.vWholeNumber': '{label} पूर्ण संख्या असावी.',
    'desk.vBetween': '{label} {min} ते {max} दरम्यान असावे.',
    'desk.vAmount': '{label} 350 किंवा 350.50 सारखी रक्कम असावी.',
    'desk.vPincode': 'PIN कोड सहा अंकी असतो.',
    'desk.vEmail': 'हा योग्य ईमेल पत्ता वाटत नाही.',
    'desk.vAfterStart': '{label} सुरुवातीनंतर असावे.',
    'desk.vNotPast': '{label} मागील काळातील असू शकत नाही.',
    'desk.vEnd': 'शेवट',
    'desk.vAgeOrMonths': 'वय वर्षांत लिहा, किंवा बाळासाठी महिन्यांत.',
  },
  or: {
    'desk.doneChip': 'ସରିଛି',
    'desk.allergyChip': 'ଆଲର୍ଜି',
    'desk.collectPayment': 'ପେମେଣ୍ଟ ନିଅନ୍ତୁ',
    'desk.fullName': 'ପୂରା ନାମ',
    'desk.mobileNumber': 'ମୋବାଇଲ୍ ନମ୍ବର',
    'desk.altMobile': 'ଅନ୍ୟ ମୋବାଇଲ୍',
    'desk.months': 'ମାସ',
    'desk.ageYears': 'ବୟସ (ବର୍ଷ)',
    'desk.monthsInfants': 'ମାସ (ଶିଶୁ)',
    'desk.city': 'ସହର',
    'desk.pin': 'PIN କୋଡ୍',
    'desk.guardian': 'ଅଭିଭାବକ',
    'desk.referredBy': 'ରେଫର୍ କରିଥିବା ବ୍ୟକ୍ତି',
    'desk.knownAllergies': 'ଜଣାଥିବା ଆଲର୍ଜି',
    'desk.allergiesPh': 'ପେନିସିଲିନ୍, ସଲଫା…',
    'desk.allergyHint': 'ପ୍ରତ୍ୟେକ ସ୍କ୍ରିନ୍ ଓ ପ୍ରେସକ୍ରିପସନ୍‌ରେ ନାଲି ରଙ୍ଗରେ ଦେଖାଯାଏ।',
    'desk.chronicConditions': 'ପୁରୁଣା ରୋଗ',
    'desk.chronicPh': 'ମଧୁମେହ, ଉଚ୍ଚ ରକ୍ତଚାପ…',
    'desk.category': 'ରୋଗୀ ଶ୍ରେଣୀ',
    'desk.categoryDiabetes': 'ମଧୁମେହ',
    'desk.categoryNonDiabetic': 'ମଧୁମେହ ନାହିଁ',
    'desk.categoryNotSet': 'ସ୍ଥିର ନାହିଁ',
    'desk.vCategory': 'ମଧୁମେହ କିମ୍ବା ମଧୁମେହ ନାହିଁ ବାଛନ୍ତୁ।',
    'desk.allCategories': 'ସମସ୍ତ ରୋଗୀ',
    'desk.categoryHint': 'ଗୋଟିଏ ବାଛନ୍ତୁ। ମଧୁମେହ ଓ ମଧୁମେହ ନାହିଁ ଅଲଗା ରଖାଯାଏ।',
    'desk.currentMedication': 'ବର୍ତ୍ତମାନର ଔଷଧ',
    'desk.notKnown': 'ଜଣା ନାହିଁ',
    'desk.fixRed': 'ପ୍ରଥମେ ନାଲି ଚିହ୍ନିତ ଘରଗୁଡ଼ିକ ଠିକ୍ କରନ୍ତୁ।',
    'desk.newPatient': 'ନୂଆ ରୋଗୀ',
    'desk.regNote': 'କେବଳ ନାମ, ଶ୍ରେଣୀ ଓ ଲିଙ୍ଗ ଆବଶ୍ୟକ; ବୟସ ଓ ମୋବାଇଲ୍ ଖାଲି ରଖାଯାଇପାରେ। ଲେଖିବା ସମୟରେ ହିଁ ଭୁଲ ଦେଖାଯାଏ।',
    'desk.registering': 'ରେଜିଷ୍ଟର ହେଉଛି…',
    'desk.registerPatient': 'ରୋଗୀ ରେଜିଷ୍ଟର କରନ୍ତୁ',
    'desk.registerNew': 'ନୂଆ ରୋଗୀ ରେଜିଷ୍ଟର କରନ୍ତୁ',
    'desk.registered': '{name} ରେଜିଷ୍ଟର ହେଲେ — {code}',
    'desk.registeredDupOne': '{code} ରେଜିଷ୍ଟର ହେଲା। ଏହା ଜଣେ ପୁରୁଣା ରୋଗୀଙ୍କ ପରି ଲାଗୁଛି।',
    'desk.registeredDupOther': '{code} ରେଜିଷ୍ଟର ହେଲା। ଏହା {n} ଜଣ ପୁରୁଣା ରୋଗୀଙ୍କ ପରି ଲାଗୁଛି।',
    'desk.registeredSimilar_one': '{code} ରେଜିଷ୍ଟର ହେଲା। {n}ଟି ସମାନ ରେକର୍ଡ ପୂର୍ବରୁ ଅଛି।',
    'desk.registeredSimilar_other': '{code} ରେଜିଷ୍ଟର ହେଲା। {n}ଟି ସମାନ ରେକର୍ଡ ପୂର୍ବରୁ ଅଛି।',
    'desk.couldNotRegister': 'ରେଜିଷ୍ଟର ହୋଇପାରିଲା ନାହିଁ।',
    'desk.quickNote': 'ବୁକିଂ ପାଇଁ ଏତିକି ଯଥେଷ୍ଟ। ବାକି ତଥ୍ୟ ପରେ ତାଙ୍କ ରେକର୍ଡରେ ଯୋଡ଼ାଯାଇପାରିବ।',
    'desk.registerPick': 'ରେଜିଷ୍ଟର କରି ବାଛନ୍ତୁ',
    'desk.startVisit': 'ପରିଦର୍ଶନ ଆରମ୍ଭ କରନ୍ତୁ',
    'desk.opening': 'ଖୋଲୁଛି…',
    'desk.openVisitPrint': 'ପରିଦର୍ଶନ ଖୋଲନ୍ତୁ ଓ ଟୋକେନ୍ ପ୍ରିଣ୍ଟ କରନ୍ତୁ',
    'desk.change': 'ବଦଳାନ୍ତୁ',
    'desk.viewFullRecord': 'ପୂରା ରେକର୍ଡ ଦେଖନ୍ତୁ',
    'desk.samePerson': 'ଏହା କଣ ସେହି ବ୍ୟକ୍ତି?',
    'desk.dupNote': 'ଏହି ରେକର୍ଡଗୁଡ଼ିକରେ ପୂର୍ବରୁ ଏହି ମୋବାଇଲ୍ ନମ୍ବର ବା ନାମ ଅଛି।',
    'desk.lookItUp': 'ଖୋଲି ଦେଖନ୍ତୁ',
    'desk.dupAdvice': 'ଏଥିମଧ୍ୟରୁ କେହି ଯଦି ଆପଣଙ୍କ ସାମ୍ନାରେ ଥିବା ରୋଗୀ, ତେବେ ସେହି ରେକର୍ଡ ବ୍ୟବହାର କରନ୍ତୁ ଏବଂ ଏବେ ତିଆରି ହୋଇଥିବା ରେକର୍ଡ ହଟାଇବାକୁ ଆଡମିନିଷ୍ଟ୍ରେଟରଙ୍କୁ କୁହନ୍ତୁ — ଇତିହାସ ରେକର୍ଡ ସହ ଯୋଡ଼ା, ନାମ ସହ ନୁହେଁ।',
    'desk.differentPerson': 'ଅଲଗା ବ୍ୟକ୍ତି, ଆଗକୁ ବଢ଼ନ୍ତୁ',
    'desk.allergyValue': 'ଆଲର୍ଜି: {text}',
    'desk.chronicValue': 'ପୁରୁଣା ରୋଗ: {text}',
    'desk.followUpRate': 'ଫଲୋ-ଅପ୍ ଫି, {n} ଦିନ ଭିତରେ',
    'desk.emergencyOverrides': 'ଜରୁରୀକାଳୀନ ହେଲେ ଏହା ଲାଗୁ ହେବ ନାହିଁ — ପରିଦର୍ଶନର ଫି କନସଲଟେସନ୍ ହାରରେ ଲାଗିବ।',
    'desk.chiefComplaint': 'ମୁଖ୍ୟ ଅସୁବିଧା',
    'desk.complaintPh': 'ମୁଣ୍ଡବିନ୍ଧା, 3 ଦିନ ହେଲା',
    'desk.alreadyInQueue': '{name} ପୂର୍ବରୁ ଧାଡ଼ିରେ ଅଛନ୍ତି — ଟୋକେନ୍ #{token}',
    'desk.tokenFor': 'ଟୋକେନ୍ #{token} — {name}',
    'desk.couldNotOpenVisit': 'ପରିଦର୍ଶନ ଖୋଲିପାରିଲା ନାହିଁ।',
    'desk.searchPatients': 'ରୋଗୀ ଖୋଜନ୍ତୁ',
    'desk.searchPh': 'ମୋବାଇଲ୍ ନମ୍ବର, ନାମ ବା ରୋଗୀ କୋଡ୍',
    'desk.clear': 'ସଫା କରନ୍ତୁ',
    'desk.matches': 'ମିଳିଥିବା ରୋଗୀ',
    'desk.checkAgeNote': 'ବାଛିବା ପୂର୍ବରୁ ବୟସ ଓ ଶେଷ ପରିଦର୍ଶନ ଦେଖନ୍ତୁ। ଅନେକ ସମୟରେ ପୂରା ପରିବାରର ଗୋଟିଏ ମୋବାଇଲ୍ ନମ୍ବର ଥାଏ।',
    'desk.visitCount_one': '{n} ପରିଦର୍ଶନ',
    'desk.visitCount_other': '{n} ପରିଦର୍ଶନ',
    'desk.lastOn': 'ଶେଷ {date}',
    'desk.noMatch': '“{q}” ସହ ମେଳ ଖାଉଥିବା କୌଣସି ରୋଗୀ ନାହାନ୍ତି',
    'desk.todaysQueue': 'ଆଜିର ଧାଡ଼ି',
    'desk.queueOn': 'ଧାଡ଼ି · {date}',
    'desk.countOf': '{total} ରୁ {n}',
    'desk.waitingCount': '{n} ଅପେକ୍ଷାରେ',
    'desk.nowAndWaiting': 'ଏବେ {token} · {n} ଅପେକ୍ଷାରେ',
    'desk.queueDate': 'ଧାଡ଼ିର ତାରିଖ',
    'desk.couldNotLoadDay': 'ସେହି ଦିନର ତାଲିକା ଲୋଡ୍ ହୋଇପାରିଲା ନାହିଁ।',
    'desk.loadingDay': '{date} ଲୋଡ୍ ହେଉଛି…',
    'desk.noVisitsToday': 'ଆଜି ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ପରିଦର୍ଶନ ନାହିଁ',
    'desk.noVisitsOn': '{date} ରେ କୌଣସି ପରିଦର୍ଶନ ନାହିଁ',
    'desk.searchAboveHint': 'ରେକର୍ଡରେ ଥିବା ଯେକୌଣସି ରୋଗୀଙ୍କୁ ଖୋଜିବାକୁ ଉପରେ ଖୋଜନ୍ତୁ।',
    'desk.noFilterMatch': 'ଏହି ଫିଲ୍ଟରରେ କେହି ମିଳିଲେ ନାହିଁ',
    'desk.doctorQueue': '{name}ଙ୍କ ଧାଡ଼ି',
    'desk.vitals': 'ଭାଇଟାଲ୍ସ',
    'desk.record': 'ରେକର୍ଡ',
    'desk.unpaidPill': 'ବାକି',
    'desk.filterStatus': 'ସ୍ଥିତି ଅନୁସାରେ ଛାଣନ୍ତୁ',
    'desk.filterDoctor': 'ଡାକ୍ତର ଅନୁସାରେ ଛାଣନ୍ତୁ',
    'desk.filterPayment': 'ପେମେଣ୍ଟ ଅନୁସାରେ ଛାଣନ୍ତୁ',
    'desk.filterVisitType': 'ପରିଦର୍ଶନ ପ୍ରକାର ଅନୁସାରେ ଛାଣନ୍ତୁ',
    'desk.filterRecords': 'ରେକର୍ଡ ଛାଣନ୍ତୁ',
    'desk.allDoctors': 'ସମସ୍ତ ଡାକ୍ତର',
    'desk.allDoctorsCount': 'ସମସ୍ତ ଡାକ୍ତର · {n}',
    'desk.anyPayment': 'ଯେକୌଣସି ପେମେଣ୍ଟ',
    'desk.anyVisitType': 'ଯେକୌଣସି ପ୍ରକାର ପରିଦର୍ଶନ',
    'desk.anyStatus': 'ଯେକୌଣସି ସ୍ଥିତି',
    'desk.refunded': 'ଫେରସ୍ତ ହୋଇଛି',
    'desk.moreFilters': 'ଅଧିକ ଫିଲ୍ଟର',
    'desk.clearFilters': 'ଫିଲ୍ଟର ହଟାନ୍ତୁ',
    'desk.fromDate': 'ଆରମ୍ଭ ତାରିଖ',
    'desk.toDate': 'ଶେଷ ତାରିଖ',
    'desk.diagnosis': 'ନିଦାନ',
    'desk.diagnosisContains': 'ନିଦାନରେ ଥିବା',
    'desk.patientNotFound': 'ରୋଗୀ ମିଳିଲେ ନାହିଁ।',
    'desk.bookAppointment': 'ଆପଏଣ୍ଟମେଣ୍ଟ ବୁକ୍ କରନ୍ତୁ',
    'desk.editPatient': 'ରୋଗୀ ତଥ୍ୟ ବଦଳାନ୍ତୁ',
    'desk.fullRecord': 'ପୂରା ରେକର୍ଡ',
    'desk.noVisitsYet': 'ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ପରିଦର୍ଶନ ଲେଖାଯାଇନାହିଁ।',
    'desk.visitNo': 'ପରିଦର୍ଶନ {n}',
    'desk.recordUpdated': 'ରୋଗୀ ରେକର୍ଡ ଅପଡେଟ୍ ହେଲା।',
    'desk.couldNotSave': 'ସେଭ୍ ହୋଇପାରିଲା ନାହିଁ।',
    'desk.saveChanges': 'ପରିବର୍ତ୍ତନ ସେଭ୍ କରନ୍ତୁ',
    'desk.auditNote': 'ପ୍ରତ୍ୟେକ ପରିବର୍ତ୍ତନ ପୂର୍ବ ତଥ୍ୟ ସହ ଅଡିଟ୍ ଲଗ୍‌ରେ ଲେଖାଯାଏ, ତେଣୁ କୌଣସି ସଂଶୋଧନ ଚୁପଚାପ୍ ହୁଏ ନାହିଁ।',
    'desk.allergyOnFile': 'ରେକର୍ଡରେ ଆଲର୍ଜି: {text}',
    'desk.records': 'ରେକର୍ଡ',
    'desk.timeline': 'ଟାଇମଲାଇନ୍ — {name}',
    'desk.timelineSub': 'ଏହି ରୋଗୀଙ୍କ ଫାଇଲର ପ୍ରତ୍ୟେକ ପରିଦର୍ଶନ।',
    'desk.timelineTitle': 'ଟାଇମଲାଇନ୍',
    'desk.timelineFor': '{name}ଙ୍କ ଟାଇମଲାଇନ୍',
    'desk.openRecordOf': '{name}ଙ୍କ ରେକର୍ଡ ଖୋଲନ୍ତୁ',
    'desk.thisPatient': 'ଏହି ରୋଗୀ',
    'desk.allVisits': 'ସମସ୍ତ ପରିଦର୍ଶନ',
    'desk.searchRecords': 'ରେକର୍ଡ ଖୋଜନ୍ତୁ',
    'desk.recordsPh': 'ନାମ, ମୋବାଇଲ୍ ବା କୋଡ୍',
    'desk.exportCsv': 'CSV ଏକ୍ସପୋର୍ଟ କରନ୍ତୁ',
    'desk.exportFailed': 'ଏକ୍ସପୋର୍ଟ ହୋଇପାରିଲା ନାହିଁ।',
    'desk.couldNotLoadRecords': 'ରେକର୍ଡ ଲୋଡ୍ ହୋଇପାରିଲା ନାହିଁ।',
    'desk.noVisitsMatch': 'କୌଣସି ପରିଦର୍ଶନ ମିଳିଲା ନାହିଁ।',
    'desk.serialAria': 'କ୍ରମ {token}, {name}, {status}',
    'desk.nthVisit': 'ପରିଦର୍ଶନ ନଂ. {n}',
    'desk.notBilled': 'ବିଲ୍ ହୋଇନାହିଁ',
    'desk.moneyRefunded': '{fee} · ଫେରସ୍ତ ହୋଇଛି',
    'desk.moneyPaid': '{fee} · ଦେୟ ଦିଆଗଲା',
    'desk.moneyPart': '{fee} ରୁ {paid} · {due} ବାକି',
    'desk.moneyUnpaid': '{fee} · ବାକି',
    'desk.prev': 'ପୂର୍ବ',
    'desk.pageOf': 'ପୃଷ୍ଠା {page} / {pages}',
    'desk.vRequired': '{label} ଆବଶ୍ୟକ।',
    'desk.vTooShort': '{label} ବହୁତ ଛୋଟ।',
    'desk.vTooLong': '{label} ବହୁତ ଲମ୍ବା।',
    'desk.vTooLongCount': '{label} ବହୁତ ଲମ୍ବା ({n}/{max})।',
    'desk.vNameChars': '{label} ରେ କେବଳ ଅକ୍ଷର, ସ୍ପେସ୍, ବିନ୍ଦୁ ଓ ହାଇଫେନ୍ ରହିପାରିବ।',
    'desk.vMobileRequired': 'ମୋବାଇଲ୍ ନମ୍ବର ଆବଶ୍ୟକ।',
    'desk.vDigitsOnly': 'କେବଳ ଅଙ୍କ ଲେଖନ୍ତୁ।',
    'desk.vMobileDigits': '10ଟି ଅଙ୍କ ଲେଖନ୍ତୁ (ଏବେ {n}ଟି ଅଛି)।',
    'desk.vMobileStart': 'ଭାରତୀୟ ମୋବାଇଲ୍ ନମ୍ବର 6, 7, 8 ବା 9ରେ ଆରମ୍ଭ ହୁଏ।',
    'desk.vWholeNumber': '{label} ଏକ ପୂର୍ଣ୍ଣ ସଂଖ୍ୟା ହେବା ଉଚିତ।',
    'desk.vBetween': '{label} {min} ରୁ {max} ମଧ୍ୟରେ ହେବା ଉଚିତ।',
    'desk.vAmount': '{label} 350 ବା 350.50 ପରି ରାଶି ହେବା ଉଚିତ।',
    'desk.vPincode': 'PIN କୋଡ୍ ଛଅ ଅଙ୍କର ହୁଏ।',
    'desk.vEmail': 'ଏହା ଠିକ୍ ଇମେଲ୍ ଠିକଣା ପରି ଲାଗୁନାହିଁ।',
    'desk.vAfterStart': '{label} ଆରମ୍ଭ ପରେ ହେବା ଉଚିତ।',
    'desk.vNotPast': '{label} ଅତୀତର ହୋଇପାରିବ ନାହିଁ।',
    'desk.vEnd': 'ଶେଷ',
    'desk.vAgeOrMonths': 'ବୟସ ବର୍ଷରେ ଲେଖନ୍ତୁ, କିମ୍ବା ଶିଶୁ ପାଇଁ ମାସରେ।',
  },
})
